import { getLocaleISOString } from './getLocaleISOString';
import { MULTI_SELECT_ELEMENT, multiSelectOptionValue, splitMultiSelectValue } from '@repo/lib';

const ADDRESS_COMPARE_KEYS = ['line1', 'line2', 'city', 'region', 'postalCode', 'country'];

// Accepts a list, a repeated query parameter (array) or a comma-joined string,
// and keeps only option values the field actually offers, in option order.
// A preloaded selection (default, draft, URL) is cut to the field's maximum:
// the checkout only greys options out once the maximum is reached, so a longer
// list would load already over it.
function toMultiSelectValues(input, field) {
  const options = field?.options;
  const optionValues = (options ?? []).map(multiSelectOptionValue);
  const wanted = new Set(
    Array.isArray(input)
      ? input.flatMap(item =>
          typeof item === 'string' ? splitMultiSelectValue(item, options) : [],
        )
      : splitMultiSelectValue(input, options),
  );
  const chosen = optionValues.filter(optionValue => wanted.has(optionValue));
  const max = field?.maxValue?.enabled ? Number(field.maxValue.value) || 0 : 0;
  return max > 0 ? chosen.slice(0, max) : chosen;
}

function addressesEqual(a, b) {
  return ADDRESS_COMPARE_KEYS.every(k => (a?.[k] ?? undefined) === (b?.[k] ?? undefined));
}

export default function mapFieldsToState(fields, query = {}, countryCode, draftCheckout = null) {
  if (!fields || !fields.length) return {};

  const fieldsFormFields = fields.reduce((obj, item) => {
    const defaultValue = item.defaultValue?.enabled ? item.defaultValue.value : '';

    if (item.element === 'country') {
      obj[item._id] = {
        ...item,
        value: defaultValue || countryCode || 'US',
      };
      return obj;
    }

    if (item.element === 'checkbox') {
      if (defaultValue === 'true') {
        obj[item._id] = { ...item, value: true };
      } else if (defaultValue === 'false') {
        obj[item._id] = { ...item, value: false };
      } else {
        obj[item._id] = { ...item, value: defaultValue || false };
      }
      return obj;
    }

    if (item.element === 'quantity') {
      obj[item._id] = { ...item, value: Number(defaultValue) || 1 };
      return obj;
    }

    if (item.element === MULTI_SELECT_ELEMENT) {
      const defaults = item.defaultValue?.enabled ? item.defaultValue.values : [];
      obj[item._id] = { ...item, value: toMultiSelectValues(defaults, item) };
      return obj;
    }

    if (item.element === 'address') {
      const limit = item.limitBillingCountries;
      const singleAllowed =
        limit?.enabled === true && Array.isArray(limit.countries) && limit.countries.length === 1
          ? limit.countries[0]
          : null;
      obj[item._id] = {
        ...item,
        value: {
          billing: { country: singleAllowed || item.defaultCountry || countryCode || 'US' },
        },
      };
      return obj;
    }

    obj[item._id] = { ...item, value: defaultValue };
    return obj;
  }, {});

  // Pre-populate from draft checkout data if available
  if (draftCheckout?.fields) {
    draftCheckout.fields.forEach(draftField => {
      /**
       * It is possible for a page to delete a field that existed in a draft checkout.
       * Filter out any null fields to avoid errors.
       */
      if (draftField.fieldId == null) {
        return;
      }

      // Handle both object and ID formats for fieldId
      const fieldId = draftField.fieldId?._id != null ? draftField.fieldId._id : draftField.fieldId;

      const field = Object.values(fieldsFormFields).find(field => field._id === fieldId);

      if (!field) {
        return;
      }

      if (field.element === MULTI_SELECT_ELEMENT) {
        const stored = Array.isArray(draftField.values) ? draftField.values : draftField.value;
        fieldsFormFields[field._id].value = toMultiSelectValues(stored, field);
        return;
      }

      if (field.element === 'address') {
        // Address fields persist value as a formatted string; the structured
        // billing/shipping objects live in meta. Reconstruct the form shape
        // and re-derive sameAsShipping (dropped at persist time).
        const billing = draftField.meta?.billing;
        const shipping = draftField.meta?.shipping;
        if (billing || shipping) {
          const restored = {
            ...(billing && { billing }),
            ...(shipping && { shipping }),
          };
          if (billing && shipping) {
            restored.sameAsShipping = addressesEqual(billing, shipping);
          }
          fieldsFormFields[field._id].value = restored;
        }
        return;
      }

      if (draftField.value !== undefined && draftField.value !== null) {
        if (field.element === 'checkbox') {
          fieldsFormFields[field._id].value =
            draftField.value === 'true' || draftField.value === true;
        } else if (field.element === 'quantity') {
          fieldsFormFields[field._id].value = Number(draftField.value) || 1;
        } else if (field.element === 'date-time') {
          fieldsFormFields[field._id].value =
            getLocaleISOString(draftField.value) || draftField.value;
        } else if (field.element === 'date') {
          fieldsFormFields[field._id].value =
            getLocaleISOString(draftField.value)?.split('T')?.[0] || draftField.value;
        } else if (field.element === 'phone') {
          // For phone fields, store the value as-is since it's already in proper format from draft
          fieldsFormFields[field._id].value = draftField.value;
        } else {
          fieldsFormFields[field._id].value = draftField.value;
        }
      }
    });
  }

  // match params to form field references (query params override draft data)
  Object.entries(query).forEach(([key, value]) => {
    const field = Object.values(fieldsFormFields).find(field => field.reference === key);

    // address params carry a formatted display string that can't be turned
    // back into the structured address value — leave the initial state alone
    if (field && field.element !== 'address') {
      if (field.element === MULTI_SELECT_ELEMENT) {
        fieldsFormFields[field._id].value = toMultiSelectValues(value, field);
        return;
      }

      // For phone fields, properly decode the URL component
      if (field.element === 'phone') {
        fieldsFormFields[field._id].value = decodeURIComponent(value)
          .replace(/^\s+/, '+')
          .replace(/^00/, '+');
      } else if (field.element === 'date-time') {
        fieldsFormFields[field._id].value = getLocaleISOString(value) || value;
      } else if (field.element === 'date') {
        fieldsFormFields[field._id].value = getLocaleISOString(value)?.split('T')?.[0] || value;
      } else {
        fieldsFormFields[field._id].value = value;
      }
    }
  });

  return fieldsFormFields;
}
